import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    Dialog,
    Empty,
    Image,
    Input,
    NavBar,
    Popup,
    PullToRefresh,
    SearchBar,
    Selector,
    SpinLoading,
    TextArea,
    Toast
} from 'antd-mobile';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import API_ROUTES from '../api';
import Icon from '../icon';
import './index.css';

const MEDIA_TYPE_OPTIONS = [
    { label: '全部', value: 'all' },
    { label: '图片', value: '1' },
    { label: '视频', value: '2' },
];

// 队员身份排序规则：常驻队员优先，其次非正式、离队、临时，和聚餐/喜事选择队员保持一致。
const getMemberTypeMeta = (tempMember) => {
    if (tempMember === null || tempMember === undefined || tempMember === '') return { label: '常驻', order: 0 };
    if (Number(tempMember) === 3) return { label: '非正式', order: 1 };
    if (Number(tempMember) === 4) return { label: '离队', order: 2 };
    if (Number(tempMember) === 1) return { label: '临时', order: 3 };
    return { label: '其他', order: 4 };
};

const normalizeMemberRecords = (records) => {
    return (Array.isArray(records) ? records : [])
        .filter(item => item?.id)
        .map(item => {
            const name = item.name || item.memberName || item.userName || '队友';
            const typeMeta = getMemberTypeMeta(item.tempMember ?? item.temp_member);
            return {
                label: name,
                value: item.id,
                searchText: `${name} ${typeMeta.label}`,
                typeLabel: typeMeta.label,
                typeOrder: typeMeta.order
            };
        })
        .sort((a, b) => a.typeOrder - b.typeOrder || String(a.label || '').localeCompare(String(b.label || ''), 'zh-Hans-CN'));
};

export default function Album() {
    const navigate = useNavigate();
    const location = useLocation();
    const memberInfo = useMemo(() => JSON.parse(localStorage.getItem('memberInfo')), []);
    const initialGameId = location.state?.gameId || 'all';
    const initialGameName = location.state?.gameName || '';
    const initialMemberId = location.state?.memberId || 'all';
    const initialMemberName = location.state?.memberName || '';
    const isGuest = memberInfo?.temp === '1';

    const [albumList, setAlbumList] = useState([]);
    const [gameOptions, setGameOptions] = useState([{ label: '全部场次', value: 'all' }]);
    const [memberOptions, setMemberOptions] = useState([{ label: '全部队员', value: 'all', searchText: '全部队员' }]);
    const [selectedGame, setSelectedGame] = useState(initialGameId);
    // 从个人页进入媒体中心时，直接锁定该个人页成员作为筛选条件。
    const [selectedMember, setSelectedMember] = useState(initialMemberId);
    const [memberQuickFilter, setMemberQuickFilter] = useState('all');
    const [selectedMediaType, setSelectedMediaType] = useState('all');
    const [statusFilter, setStatusFilter] = useState(memberInfo?.admin === 1 ? '0' : '0');
    const [recycleMode, setRecycleMode] = useState(false);
    const [tagName, setTagName] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    const [comments, setComments] = useState([]);
    const [commentText, setCommentText] = useState('');
    const [loading, setLoading] = useState(false);
    const [gamePickerVisible, setGamePickerVisible] = useState(false);
    const [gamePickerKeyword, setGamePickerKeyword] = useState('');
    const [gamePickerDraft, setGamePickerDraft] = useState('all');
    const [memberPickerVisible, setMemberPickerVisible] = useState(false);
    const [memberPickerKeyword, setMemberPickerKeyword] = useState('');
    const [memberPickerDraft, setMemberPickerDraft] = useState('all');
    const [memberNameMap, setMemberNameMap] = useState({});
    const [pendingAuditCount, setPendingAuditCount] = useState(0);
    const [tagOptions, setTagOptions] = useState([]);
    const [editVisible, setEditVisible] = useState(false);
    const [editForm, setEditForm] = useState({
        mediaId: '',
        gameId: '',
        eventId: '',
        memberId: '',
        tagIds: [],
        remark: '',
        mediaType: 1
    });
    const [editEventOptions, setEditEventOptions] = useState([{ label: '不绑定事件', value: '' }]);
    const [editPickerType, setEditPickerType] = useState('');
    const [editPickerSearch, setEditPickerSearch] = useState('');
    const [editPickerDraftValue, setEditPickerDraftValue] = useState('');
    const [editSubmitting, setEditSubmitting] = useState(false);

    const statusOptions = useMemo(() => {
        const base = [{ label: '已展示', value: '0' }];
        if (isGuest) return base;
        if (memberInfo?.admin === 1) {
            base.push({ label: '待审核', value: '1' });
            base.push({ label: '未通过', value: '2' });
        } else {
            base.push({ label: '我的提交', value: 'mine' });
        }
        return base;
    }, [isGuest, memberInfo?.admin]);

    const getMediaUrl = (item) => item?.mediaUrl || item?.media_url || item?.url || '';

    const getMediaType = (item) => Number(item?.mediaType ?? item?.media_type);

    const getLikeCount = (item) => Number(item?.likeCount ?? item?.like_count ?? 0);

    const getDislikeCount = (item) => Number(item?.dislikeCount ?? item?.dislike_count ?? 0);

    const getCommentCount = (item) => Number(item?.commentCount ?? item?.comment_count ?? 0);

    const getViewCount = (item) => Number(item?.viewCount ?? item?.view_count ?? 0);

    const getUploadMemberId = (item) => item?.uploadMemberId || item?.upload_member_id || item?.uploadUserId || item?.upload_user_id;

    const getBindMemberId = (item) => item?.memberId || item?.member_id;

    const getBindGameId = (item) => item?.gameId || item?.game_id;

    const getFileName = (item) => item?.fileName || item?.file_name || '';

    const eventDescriptions = useMemo(() => ({
        "1": "开始排位",
        "2": "比赛结束",
        "3": "运动战(其他)",
        "4": "乌龙球",
        "5": "黄牌",
        "6": "红牌",
        "7": "半场结束",
        "8": "半场开始",
        "9": "点球未进",
        "10": "半场开始",
        "11": "运动战(头球)",
        "12": "点球(右脚)",
        "13": "运动战(左脚)",
        "14": "运动战(右脚)",
        "15": "任意球(右脚)",
        "16": "角球(右脚)",
        "17": "上下场",
        "18": "暂停",
        "19": "任意球(左脚)",
        "20": "角球(左脚)",
        "21": "点球(左脚)"
    }), []);

    const normalizeEmptyValue = (value) => {
        return value === 'all' || value === null || value === undefined ? '' : value;
    };

    const getDisplayTitle = (item) => {
        const mediaType = getMediaType(item);
        // 上传时没有填写展示名称时，使用后端保存的原始文件名兜底。
        return item?.remark || item?.title || getFileName(item) || (mediaType === 2 ? '视频记录' : '图片记录');
    };

    const getStatusText = (item) => {
        const status = Number(item?.status);
        if (status === 1) return '待审核';
        if (status === 2) return '未通过';
        if (status === 3) return '已隐藏';
        return '已展示';
    };

    const getAuditRemark = (item) => item?.auditRemark || item?.audit_remark || '';

    const canEditMedia = (item) => {
        if (isGuest || !item) return false;
        if (memberInfo?.admin === 1) return true;
        return String(getUploadMemberId(item) || '') === String(memberInfo?.id || '');
    };

    const isReadonlySubmission = (item) => {
        const isOwnMedia = String(getUploadMemberId(item) || '') === String(memberInfo?.id || '');
        return memberInfo?.admin !== 1 && isOwnMedia && (statusFilter === 'mine' || Number(item?.status) !== 0);
    };

    const getUploadMemberName = (item) => {
        const backendName = item?.uploadMemberName || item?.upload_member_name || item?.uploadUserName || item?.upload_user_name;
        if (backendName) return backendName;
        const uploadMemberId = getUploadMemberId(item);
        return uploadMemberId ? memberNameMap[String(uploadMemberId)] || '队友' : '队友';
    };

    const getBindMemberNames = (item) => {
        const backendName = item?.memberName || item?.member_name;
        if (backendName) return backendName;
        const memberId = getBindMemberId(item);
        if (!memberId) return '';
        return String(memberId)
            .split(',')
            .map(id => memberNameMap[String(id).trim()])
            .filter(Boolean)
            .join('、');
    };

    const getBindGameName = (item) => {
        const backendName = item?.gameName || item?.game_name;
        if (backendName) return backendName;
        const gameId = getBindGameId(item);
        if (!gameId) return '';
        return gameOptions.find(option => String(option.value) === String(gameId))?.label || '';
    };

    const getMediaTagIds = (item) => item?.tagIds || item?.tag_ids || [];

    const getMediaTagNames = (item) => {
        const backendNames = item?.tagNames || item?.tag_names;
        if (Array.isArray(backendNames) && backendNames.length) return backendNames;
        const oldTag = item?.tagName || item?.tag_name;
        return oldTag ? String(oldTag).split(',').map(tag => tag.trim()).filter(Boolean) : [];
    };

    const formatGameDate = useCallback((item) => {
        const rawDate = item?.matchTime || item?.startDate || item?.matchDate || '';
        // 比赛筛选只展示日期，避免完整时间把 H5 弹窗选项撑得过长。
        const matchedDate = String(rawDate).match(/\d{4}-\d{2}-\d{2}/);
        return matchedDate ? matchedDate[0] : '';
    }, []);

    const buildGameLabel = useCallback((item) => {
        const dateText = formatGameDate(item);
        return `${item.gameName || '比赛'}${dateText ? ` · ${dateText}` : ''}`;
    }, [formatGameDate]);

    const selectedGameLabel = useMemo(() => {
        if (selectedGame === initialGameId && initialGameName) return initialGameName;
        return gameOptions.find(item => item.value === selectedGame)?.label || '全部场次';
    }, [gameOptions, initialGameId, initialGameName, selectedGame]);

    const selectedMemberLabel = useMemo(() => {
        if (memberQuickFilter === 'mine') return '我的相关';
        if (selectedMember === initialMemberId && initialMemberName) return initialMemberName;
        return memberOptions.find(item => item.value === selectedMember)?.label || '全部队员';
    }, [initialMemberId, initialMemberName, memberOptions, memberQuickFilter, selectedMember]);

    const filteredGameOptions = useMemo(() => {
        const keyword = gamePickerKeyword.trim().toLowerCase();
        if (!keyword) return gameOptions;
        // 比赛筛选弹窗在前端做轻量搜索，避免每次输入都请求接口。
        return gameOptions.filter(item => String(item.label || '').toLowerCase().includes(keyword));
    }, [gameOptions, gamePickerKeyword]);

    const filteredMemberOptions = useMemo(() => {
        const keyword = memberPickerKeyword.trim().toLowerCase();
        if (!keyword) return memberOptions;
        // 队员筛选弹窗复用上传页的本地搜索方式，按队员姓名过滤。
        return memberOptions.filter(item => String(item.searchText || item.label || '').toLowerCase().includes(keyword));
    }, [memberOptions, memberPickerKeyword]);

    const editGameOptions = useMemo(() => [
        { label: '不绑定比赛', value: '', searchText: '不绑定比赛' },
        ...gameOptions
            .filter(item => item.value !== 'all')
            .map(item => ({ ...item, searchText: item.label }))
    ], [gameOptions]);

    const editMemberOptions = useMemo(() => [
        { label: '不绑定队员', value: '', searchText: '不绑定队员' },
        ...memberOptions
            .filter(item => item.value !== 'all')
            .map(item => ({ ...item, searchText: item.searchText || item.label }))
    ], [memberOptions]);

    const getTagList = useCallback((mediaType) => {
        axios.get(API_ROUTES.album_tag_list, {
            params: { mediaType }
        }).then((response) => {
            const records = response.data?.data || [];
            setTagOptions(Array.isArray(records) ? records : []);
            // 标签删除或媒体类型变化后，同步清理编辑表单里的无效选中项。
            setEditForm(prev => ({
                ...prev,
                tagIds: (prev.tagIds || []).filter(id => records.some(tag => String(tag.id) === String(id)))
            }));
        }).catch(() => {
            Toast.show({ icon: 'fail', content: '标签加载失败' });
        });
    }, []);

    const buildThumbUrl = (item) => {
        const url = getMediaUrl(item);
        if (!url) return '';
        // 图片列表统一使用 OSS 缩略图，视频列表统一使用 OSS 截帧缩略图。
        if (getMediaType(item) === 2) {
            return `${url}${url.includes('?') ? '&' : '?'}x-oss-process=video/snapshot,t_10000,m_fast`;
        }
        return url + Icon.ImageFooter;
    };

    const formatEventTime = useCallback((value) => {
        if (value === null || value === undefined || value === '') return '时间未记';
        const seconds = Number(value);
        if (Number.isNaN(seconds)) return String(value);
        const minutes = Math.floor(seconds / 60);
        const remainSeconds = seconds % 60;
        return `${minutes}:${String(remainSeconds).padStart(2, '0')}`;
    }, []);

    const hasEventTime = useCallback((item) => {
        return !!item?.eventTimeName || !(item?.eventTime === null || item?.eventTime === undefined || item?.eventTime === '');
    }, []);

    const getEventTitle = useCallback((item) => {
        if (!item) return '比赛事件';
        return eventDescriptions[String(item.eventId)] || item.eventInfo || item.eventName || '比赛事件';
    }, [eventDescriptions]);

    const getEventKind = (item) => {
        const eid = String(item?.eventId);
        if (["3", "4", "9", "11", "12", "13", "14", "15", "16", "19", "20", "21"].includes(eid)) return 'goal';
        if (["5", "6"].includes(eid)) return 'card';
        if (["17"].includes(eid)) return 'sub';
        if (["1", "2", "7", "8", "10", "18"].includes(eid)) return 'status';
        return 'normal';
    };

    const getMemberIds = useCallback((ids) => {
        if (!ids || ids === '1') return [];
        return String(ids)
            .split(',')
            .map(id => id.trim())
            .filter(id => id && id !== '1');
    }, []);

    const uniqueIds = (ids) => Array.from(new Set(ids.filter(Boolean)));

    const getMemberNames = useCallback((ids) => {
        const memberIds = getMemberIds(ids);
        if (!memberIds.length) return '';
        return memberIds
            .map(id => editMemberOptions.find(member => String(member.value) === String(id))?.label)
            .filter(Boolean)
            .join('、');
    }, [editMemberOptions, getMemberIds]);

    const getEventMemberId = (event) => event?.memberId || event?.member_id;
    const getEventAssistMemberId = (event) => event?.assistMemberId || event?.assist_member_id;
    const getEventUpDownMemberId = (event) => event?.upDownMemberId || event?.up_down_member_id;

    const getEventRelatedMemberIds = useCallback((event) => {
        if (!event) return [];
        // 修改绑定事件时，自动把事件中的上场人、下场人和助攻人同步到关联队员。
        return uniqueIds([
            ...getMemberIds(getEventMemberId(event)),
            ...getMemberIds(getEventAssistMemberId(event)),
            ...getMemberIds(getEventUpDownMemberId(event))
        ]);
    }, [getMemberIds]);

    const getGameOptions = useCallback(() => {
        axios.get(API_ROUTES.album_dropdown_games).then((response) => {
            const records = response.data?.data || [];
            // 相册筛选只需要比赛下拉数据，使用轻量接口避免首页筛选加载过慢。
            const options = records.map(item => ({
                label: buildGameLabel(item),
                value: item.id
            }));
            setGameOptions([{ label: '全部场次', value: 'all' }, ...options]);
        }).catch(() => {
            setGameOptions([{ label: '全部场次', value: 'all' }]);
        });
    }, [buildGameLabel]);

    const getMemberOptions = useCallback(() => {
        const buildMap = (records) => {
            const nextMap = {};
            const options = normalizeMemberRecords(records);
            options.forEach(item => {
                nextMap[String(item.value)] = item.label;
            });
            setMemberNameMap(nextMap);
            setMemberOptions([{ label: '全部队员', value: 'all', searchText: '全部队员' }, ...options]);
        };

        const yearNum = new Date().getFullYear();
        axios.get(API_ROUTES.soccer_list, {
            params: { year: yearNum, type: 1 }
        }).then((response) => {
            const data = response.data?.data || [];
            // 与上传页选择队员保持一致：成员接口返回分组结构时先拍平成选项。
            const records = Array.isArray(data) && data[0]?.items
                ? data.flatMap(group => group.items || [])
                : data;
            buildMap(records);
            
        }).catch(() => {
            setMemberNameMap({});
            setMemberOptions([{ label: '全部队员', value: 'all', searchText: '全部队员' }]);
        });
      
    }, []);

    const buildEventOptionLabel = useCallback((item, index) => {
        const timeText = hasEventTime(item) ? `${item.eventTimeName || formatEventTime(item.eventTime)} ` : '';
        const playerText = item.userName ? ` · ${item.userName}` : '';
        return `${index + 1}. ${timeText}${getEventTitle(item)}${playerText}`;
    }, [formatEventTime, getEventTitle, hasEventTime]);

    const getEditEventOptions = useCallback((nextGameId) => {
        if (!nextGameId) {
            setEditEventOptions([{ label: '不绑定事件', value: '' }]);
            return;
        }
        axios.get(API_ROUTES.album_dropdown_events, {
            params: { gameId: nextGameId }
        }).then((response) => {
            const records = response.data?.data || [];
            // 编辑信息里的事件选择和上传页保持一致，按比赛内时间正序展示。
            const sortedRecords = [...records].sort((a, b) => {
                const aEventTime = a.eventTime === null || a.eventTime === undefined || a.eventTime === '' ? null : Number(a.eventTime);
                const bEventTime = b.eventTime === null || b.eventTime === undefined || b.eventTime === '' ? null : Number(b.eventTime);
                if (aEventTime !== null && bEventTime !== null && aEventTime !== bEventTime) {
                    return aEventTime - bEventTime;
                }
                const aCurrentTime = a.currentTime ? new Date(a.currentTime).getTime() : 0;
                const bCurrentTime = b.currentTime ? new Date(b.currentTime).getTime() : 0;
                return aCurrentTime - bCurrentTime;
            });
            const options = sortedRecords.map((item, index) => ({
                label: buildEventOptionLabel(item, index),
                value: item.id,
                raw: item,
                searchText: [
                    getEventTitle(item),
                    item.userName,
                    item.assistMemberName,
                    item.upDownName,
                    item.teamName
                ].filter(Boolean).join(' ')
            }));
            setEditEventOptions([{ label: '不绑定事件', value: '' }, ...options]);
        }).catch(() => {
            Toast.show({ icon: 'fail', content: '事件列表加载失败' });
            setEditEventOptions([{ label: '不绑定事件', value: '' }]);
        });
    }, [buildEventOptionLabel, getEventTitle]);

    const getAlbumList = useCallback(() => {
        setLoading(true);
        const baseParams = {
            current: 1,
            size: 60,
            gameId: selectedGame === 'all' ? undefined : selectedGame,
            // “我的”与具体队员筛选互斥：我的使用当前登录人 id，具体队员只在普通队员筛选时生效。
            memberId: memberQuickFilter === 'mine' ? memberInfo?.id : (selectedMember === 'all' ? undefined : selectedMember),
            mediaType: selectedMediaType === 'all' ? undefined : selectedMediaType,
            tagName: tagName.trim() || undefined,
            tagId: undefined,
            currentMemberId: memberInfo?.id
        };

        const activeStatus = recycleMode ? '3' : statusFilter;
        const request = activeStatus === 'mine'
            // 普通用户的“我的提交”需要覆盖全部审核状态，所以分状态请求后在前端合并。
            ? Promise.all(['0', '1', '2', '3'].map(status => axios.get(API_ROUTES.album_list, {
                params: {
                    ...baseParams,
                    status,
                    uploadMemberId: memberInfo?.id
                }
            }))).then((responses) => ({
                data: {
                    data: responses.flatMap(response => {
                        const data = response.data?.data;
                        const records = data?.records || data || [];
                        return Array.isArray(records) ? records : [];
                    })
                }
            }))
            : axios.get(API_ROUTES.album_list, {
            params: {
                ...baseParams,
                status: activeStatus,
            }
        });

        request.then((response) => {
            const records = response.data?.data?.records || response.data?.data || [];
            setAlbumList(Array.isArray(records) ? records : []);
        }).catch(() => {
            Toast.show({ icon: 'fail', content: '相册加载失败' });
            setAlbumList([]);
        }).finally(() => {
            setLoading(false);
        });
    }, [memberInfo?.id, memberQuickFilter, recycleMode, selectedGame, selectedMember, selectedMediaType, statusFilter, tagName]);

    const getPendingAuditCount = useCallback(() => {
        if (memberInfo?.admin !== 1) return;
        axios.get(API_ROUTES.album_list, {
            params: {
                current: 1,
                size: 1,
                status: 1,
                currentMemberId: memberInfo?.id
            }
        }).then((response) => {
            const data = response.data?.data;
            const records = data?.records || data || [];
            // 管理员提醒只需要待审核总数，分页接口没有 total 时用当前返回数量兜底。
            setPendingAuditCount(Number(data?.total ?? (Array.isArray(records) ? records.length : 0)));
        }).catch(() => {
            setPendingAuditCount(0);
        });
    }, [memberInfo?.admin, memberInfo?.id]);

    useEffect(() => {
        getGameOptions();
        getMemberOptions();
        getPendingAuditCount();
        getTagList();
    }, [getGameOptions, getMemberOptions, getPendingAuditCount, getTagList]);

    useEffect(() => {
        getAlbumList();
    }, [getAlbumList]);

    useEffect(() => {
        if (!editVisible) return;
        getEditEventOptions(editForm.gameId);
        getTagList(editForm.mediaType);
    }, [editForm.gameId, editForm.mediaType, editVisible, getEditEventOptions, getTagList]);

    const openPreview = (item) => {
        const nextViewCount = getViewCount(item) + 1;
        const nextItem = { ...item, viewCount: nextViewCount };
        // 用户点击查看媒体时先本地递增，后端返回后再用真实计数校准。
        setAlbumList(prev => prev.map(media => media.id === item.id ? nextItem : media));
        setSelectedItem(nextItem);
        setCommentText('');
        getCommentList(item.id);
        axios.post(API_ROUTES.album_view, null, {
            params: { mediaId: item.id }
        }).then((response) => {
            if (response.data?.code === 200 && response.data?.data !== undefined) {
                const realViewCount = Number(response.data.data);
                const syncedItem = { ...nextItem, viewCount: Number.isNaN(realViewCount) ? nextViewCount : realViewCount };
                setAlbumList(prev => prev.map(media => media.id === item.id ? syncedItem : media));
                setSelectedItem(prev => prev?.id === item.id ? syncedItem : prev);
            }
        }).catch(() => {
            // 浏览统计失败不影响用户查看媒体。
        });
    };

    const openGamePicker = () => {
        setGamePickerDraft(selectedGame);
        setGamePickerKeyword('');
        setGamePickerVisible(true);
    };

    const openMemberPicker = () => {
        setMemberPickerDraft(selectedMember);
        setMemberPickerKeyword('');
        setMemberPickerVisible(true);
    };

    const confirmGamePicker = () => {
        setSelectedGame(gamePickerDraft || 'all');
        setGamePickerVisible(false);
    };

    const confirmMemberPicker = () => {
        setSelectedMember(memberPickerDraft || 'all');
        // 选择具体队员后退出“我的”，避免两个 memberId 条件互相覆盖。
        setMemberQuickFilter('all');
        setMemberPickerVisible(false);
    };

    const toggleMineFilter = () => {
        setRecycleMode(false);
        if (memberQuickFilter === 'mine') {
            setMemberQuickFilter('all');
            return;
        }
        // “我的”筛选只使用当前登录人 id，清空具体队员选择，保证筛选条件唯一。
        setSelectedMember('all');
        setMemberPickerDraft('all');
        setMemberQuickFilter('mine');
    };

    const openRecycleBin = () => {
        // 回收站单独查询已隐藏影像，常规审核筛选不再展示“已隐藏”。
        setRecycleMode(true);
        setMemberQuickFilter('all');
        setSelectedMember('all');
        setMemberPickerDraft('all');
        if (statusFilter === 'mine') {
            setStatusFilter('0');
        }
    };

    const closeRecycleBin = () => {
        setRecycleMode(false);
        setStatusFilter('0');
    };

    const getOptionLabel = (options, value) => {
        const target = options.find(item => String(item.value) === String(value || ''));
        return target?.label || '';
    };

    const getMultiOptionLabel = (options, value) => {
        const ids = getMemberIds(value);
        if (!ids.length) return '';
        return ids
            .map(id => options.find(item => String(item.value) === String(id))?.label)
            .filter(Boolean)
            .join('、');
    };

    const openEditMedia = (item) => {
        if (!canEditMedia(item)) return;
        setSelectedItem(null);
        setEditForm({
            mediaId: item.id,
            gameId: normalizeEmptyValue(getBindGameId(item)),
            eventId: normalizeEmptyValue(item.eventId || item.event_id),
            memberId: normalizeEmptyValue(getBindMemberId(item)),
            tagIds: getMediaTagIds(item),
            remark: item.remark || item.title || '',
            mediaType: getMediaType(item) || 1
        });
        setEditPickerType('');
        setEditPickerSearch('');
        setEditVisible(true);
    };

    const closeEditPopup = () => {
        setEditVisible(false);
        setEditPickerType('');
        setEditPickerSearch('');
        setEditPickerDraftValue('');
    };

    const editPickerConfig = useMemo(() => ({
        game: {
            title: '选择比赛场次',
            placeholder: '搜索比赛名称或时间',
            options: editGameOptions,
            value: editForm.gameId,
            onConfirm: (value) => {
                // 切换比赛后事件需要重新选择，避免把旧比赛事件绑定到新比赛上。
                setEditForm(prev => ({ ...prev, gameId: value, eventId: '' }));
            }
        },
        event: {
            title: '选择比赛事件',
            placeholder: '搜索事件、球员或时间',
            options: editEventOptions,
            value: editForm.eventId,
            onConfirm: (value) => {
                const event = editEventOptions.find(item => item.value === value)?.raw;
                const relatedMemberIds = getEventRelatedMemberIds(event);
                setEditForm(prev => ({
                    ...prev,
                    eventId: value,
                    memberId: relatedMemberIds.length ? relatedMemberIds.join(',') : prev.memberId
                }));
            }
        },
        member: {
            title: '选择关联队员',
            placeholder: '搜索队员姓名',
            options: editMemberOptions,
            value: editForm.memberId,
            onConfirm: (value) => setEditForm(prev => ({ ...prev, memberId: value }))
        }
    }), [editEventOptions, editForm.eventId, editForm.gameId, editForm.memberId, editGameOptions, editMemberOptions, getEventRelatedMemberIds]);

    const currentEditPicker = editPickerConfig[editPickerType];

    const getEventSearchText = useCallback((event) => {
        if (!event) return '';
        return [
            getEventTitle(event),
            event.userName || getMemberNames(getEventMemberId(event)),
            event.assistMemberName || getMemberNames(getEventAssistMemberId(event)),
            event.upDownName || getMemberNames(getEventUpDownMemberId(event))
        ].filter(Boolean).join(' ');
    }, [getEventTitle, getMemberNames]);

    const filteredEditPickerOptions = useMemo(() => {
        if (!currentEditPicker) return [];
        const keyword = editPickerSearch.trim().toLowerCase();
        if (!keyword) return currentEditPicker.options;
        // 编辑弹窗的搜索只在前端做过滤，避免输入时连续请求后端。
        return currentEditPicker.options.filter(item => {
            if (editPickerType === 'event') {
                return getEventSearchText(item.raw).toLowerCase().includes(keyword);
            }
            return String(item.searchText || item.label || '').toLowerCase().includes(keyword);
        });
    }, [currentEditPicker, editPickerSearch, editPickerType, getEventSearchText]);

    const openEditPicker = (type) => {
        const config = editPickerConfig[type];
        if (!config) return;
        setEditPickerType(type);
        setEditPickerSearch('');
        setEditPickerDraftValue(type === 'member' ? getMemberIds(config.value) : config.value);
    };

    const closeEditPicker = () => {
        setEditPickerType('');
        setEditPickerSearch('');
        setEditPickerDraftValue('');
    };

    const confirmEditPicker = () => {
        if (!currentEditPicker) return;
        if (editPickerType === 'member') {
            currentEditPicker.onConfirm(uniqueIds(editPickerDraftValue).join(','));
            closeEditPicker();
            return;
        }
        currentEditPicker.onConfirm(editPickerDraftValue);
        closeEditPicker();
    };

    const toggleEditMemberDraft = (value) => {
        if (!value) {
            setEditPickerDraftValue([]);
            return;
        }
        setEditPickerDraftValue(prev => {
            const current = Array.isArray(prev) ? prev : getMemberIds(prev);
            return current.includes(value)
                ? current.filter(id => id !== value)
                : [...current, value];
        });
    };

    const isEditOptionSelected = (value) => {
        if (editPickerType === 'member') {
            return Array.isArray(editPickerDraftValue) && editPickerDraftValue.includes(value);
        }
        return editPickerDraftValue === value;
    };

    const renderEditEventOptionContent = (item) => {
        const event = item.raw;
        if (!event) return <span>{item.label}</span>;

        const kind = getEventKind(event);
        const showTime = hasEventTime(event);
        const timeText = showTime ? (event.eventTimeName || formatEventTime(event.eventTime)) : '';
        const teamText = event.teamName || '';
        const playerText = event.userName || getMemberNames(getEventMemberId(event));
        const assistName = event.assistMemberName || getMemberNames(getEventAssistMemberId(event));
        const assistText = assistName ? `助攻 ${assistName}` : '';
        const upNames = event.userName || getMemberNames(getEventMemberId(event));
        const downNames = event.upDownName || getMemberNames(getEventUpDownMemberId(event));
        const fallbackText = event.currentTime || '比赛节点';
        const metaItems = kind === 'sub'
            ? [
                upNames ? `上场 ${upNames}` : '',
                downNames ? `下场 ${downNames}` : ''
            ].filter(Boolean)
            : [playerText, assistText].filter(Boolean);

        return (
            <div className={`album-event-option-card album-event-option-card--${kind}`}>
                {showTime ? <div className="album-event-time-pill">{timeText}</div> : <div className="album-event-time-spacer" />}
                <div className="album-event-card-body">
                    <div className="album-event-title-row">
                        <span className="album-event-kind">{getEventTitle(event)}</span>
                        {teamText && <span className="album-event-team">{teamText}</span>}
                    </div>
                    <div className="album-event-meta-row">
                        {metaItems.length > 0 ? metaItems.map(text => <span key={text}>{text}</span>) : <span>{fallbackText}</span>}
                    </div>
                </div>
            </div>
        );
    };

    const renderEditPickerOptionContent = (item) => {
        if (editPickerType === 'event') {
            return renderEditEventOptionContent(item);
        }
        if (editPickerType === 'member') {
            return renderMemberOptionContent(item);
        }
        return <span>{item.label}</span>;
    };

    const renderMemberOptionContent = (item) => (
        <div className="album-member-option-content">
            <strong className="album-member-option-name">{item.label}</strong>
            {item.typeLabel && (
                <em className={`album-member-type-badge album-member-type-${item.typeOrder}`}>
                    {item.typeLabel}
                </em>
            )}
        </div>
    );

    const getSelectedEditTagNames = () => {
        return (editForm.tagIds || [])
            .map(id => tagOptions.find(tag => String(tag.id) === String(id))?.tagName)
            .filter(Boolean)
            .join(',');
    };

    const toggleEditTag = (tagId) => {
        setEditForm(prev => ({
            ...prev,
            tagIds: (prev.tagIds || []).includes(tagId)
                ? prev.tagIds.filter(id => id !== tagId)
                : [...(prev.tagIds || []), tagId]
        }));
    };

    const addEditTag = () => {
        if (memberInfo?.admin !== 1) {
            Toast.show({ content: '只有管理员可以新增标签' });
            return;
        }
        let nextTagName = '';
        Dialog.confirm({
            title: '新增标签',
            content: (
                <Input
                    className="album-form-input"
                    placeholder="例如：世界波、合影"
                    onChange={(value) => { nextTagName = value; }}
                />
            ),
            onConfirm: async () => {
                if (!nextTagName.trim()) {
                    Toast.show({ content: '请输入标签名称' });
                    return Promise.reject();
                }
                return axios.post(API_ROUTES.album_tag_add, {
                    tagName: nextTagName.trim(),
                    mediaType: editVisible ? editForm.mediaType : (selectedMediaType === 'all' ? undefined : Number(selectedMediaType))
                }, {
                    headers: { Authorization: memberInfo?.id }
                }).then((response) => {
                    if (response.data?.code === 200) {
                        const tag = response.data?.data;
                        getTagList(editVisible ? editForm.mediaType : undefined);
                        if (tag?.id) {
                            setEditForm(prev => ({
                                ...prev,
                                tagIds: Array.from(new Set([...(prev.tagIds || []), tag.id]))
                            }));
                        }
                    } else {
                        Toast.show({ icon: 'fail', content: response.data?.msg || '新增失败' });
                        return Promise.reject();
                    }
                }).catch(() => {
                    Toast.show({ icon: 'fail', content: '新增失败' });
                    return Promise.reject();
                });
            }
        });
    };

    const deleteEditTag = (tagId) => {
        if (memberInfo?.admin !== 1) {
            Toast.show({ content: '只有管理员可以删除标签' });
            return;
        }
        Dialog.confirm({
            title: '删除标签',
            content: '确认删除这个标签吗？删除后会解除已有影像的标签绑定。',
            onConfirm: () => axios.post(API_ROUTES.album_tag_delete, {
                tagId
            }, {
                headers: { Authorization: memberInfo?.id }
            }).then((response) => {
                if (response.data?.code === 200) {
                    setEditForm(prev => ({
                        ...prev,
                        tagIds: (prev.tagIds || []).filter(id => id !== tagId)
                    }));
                    getTagList(editVisible ? editForm.mediaType : undefined);
                    Toast.show({ icon: 'success', content: '标签已删除' });
                } else {
                    Toast.show({ icon: 'fail', content: response.data?.msg || '删除失败' });
                    return Promise.reject();
                }
            }).catch(() => {
                Toast.show({ icon: 'fail', content: '删除失败' });
                return Promise.reject();
            })
        });
    };

    const getCommentList = (mediaId) => {
        axios.get(API_ROUTES.album_comment_list, {
            params: { mediaId }
        }).then((response) => {
            const result = response.data?.data || [];
            setComments(Array.isArray(result) ? result : []);
        }).catch(() => {
            setComments([]);
        });
    };

    const handleReaction = (item, reactionType) => {
        const nextReaction = Number(item.currentReactionType) === reactionType ? 0 : reactionType;
        axios.post(API_ROUTES.album_reaction, {
            mediaId: item.id,
            memberId: memberInfo?.id,
            reactionType: nextReaction
        }).then((response) => {
            if (response.data?.code === 200) {
                // 点赞/点踩接口只返回操作结果，前端需要根据切换前后的状态同步更新数量展示。
                const syncReactionCount = (target) => {
                    if (!target || target.id !== item.id) return target;
                    const prevReaction = Number(target.currentReactionType || item.currentReactionType || 0);
                    const prevLikeCount = getLikeCount(target);
                    const prevDislikeCount = getDislikeCount(target);
                    const likeDelta = (nextReaction === 1 ? 1 : 0) - (prevReaction === 1 ? 1 : 0);
                    const dislikeDelta = (nextReaction === 2 ? 1 : 0) - (prevReaction === 2 ? 1 : 0);

                    return {
                        ...target,
                        currentReactionType: nextReaction,
                        likeCount: Math.max(0, prevLikeCount + likeDelta),
                        dislikeCount: Math.max(0, prevDislikeCount + dislikeDelta)
                    };
                };

                setAlbumList(prev => prev.map(syncReactionCount));
                setSelectedItem(prev => syncReactionCount(prev));
                getAlbumList();
            } else {
                Toast.show({ icon: 'fail', content: response.data?.msg || '操作失败' });
            }
        }).catch(() => {
            Toast.show({ icon: 'fail', content: '操作失败' });
        });
    };

    const submitComment = () => {
        if (!commentText.trim()) {
            Toast.show({ content: '请输入评论内容' });
            return;
        }

        axios.post(API_ROUTES.album_comment, {
            mediaId: selectedItem.id,
            memberId: memberInfo?.id,
            content: commentText.trim()
        }).then((response) => {
            if (response.data?.code === 200) {
                setCommentText('');
                getCommentList(selectedItem.id);
                getAlbumList();
            } else {
                Toast.show({ icon: 'fail', content: response.data?.msg || '评论失败' });
            }
        }).catch(() => {
            Toast.show({ icon: 'fail', content: '评论失败' });
        });
    };

    const auditMedia = (item, status) => {
        const statusTextMap = {
            0: Number(item.status) === 1 ? '审核通过' : '重新发布',
            2: '审核不通过',
            3: '删除'
        };
        let auditRemark = '';
        Dialog.confirm({
            title: status === 2 ? '填写不通过原因' : undefined,
            content: status === 2 ? (
                <div className="album-audit-reject-dialog">
                    <div>请填写不通过原因，队员会在媒体详情中看到这条说明。</div>
                    <TextArea
                        className="album-audit-reject-input"
                        placeholder="例如：视频画面不清晰，请重新上传原片"
                        rows={3}
                        onChange={(value) => { auditRemark = value; }}
                    />
                </div>
            ) : `确认${statusTextMap[status]}这条影像吗？`,
            onConfirm: () => {
                if (status === 2 && !auditRemark.trim()) {
                    Toast.show({ content: '请输入不通过原因' });
                    return Promise.reject();
                }
                return axios.post(API_ROUTES.album_audit, {
                    mediaId: item.id,
                    status,
                    auditMemberId: memberInfo?.id,
                    auditRemark: status === 2 ? auditRemark.trim() : ''
                }, {
                    headers: { Authorization: memberInfo?.id }
                }).then((response) => {
                    if (response.data?.code === 200) {
                        Toast.show({ icon: 'success', content: '操作成功' });
                        getAlbumList();
                        getPendingAuditCount();
                        setSelectedItem(null);
                    } else {
                        Toast.show({ icon: 'fail', content: response.data?.msg || '操作失败' });
                    }
                }).catch(() => {
                    Toast.show({ icon: 'fail', content: '操作失败' });
                    return Promise.reject();
                });
            }
        });
    };

    const submitEditMedia = () => {
        if (!editForm.mediaId) return;
        setEditSubmitting(true);
        axios.post(API_ROUTES.album_update, {
            mediaId: editForm.mediaId,
            gameId: editForm.gameId || '',
            eventId: editForm.eventId || '',
            memberId: editForm.memberId || '',
            tagName: getSelectedEditTagNames() || '',
            tagIds: editForm.tagIds || [],
            remark: editForm.remark?.trim() || ''
        }, {
            headers: { Authorization: memberInfo?.id }
        }).then((response) => {
            if (response.data?.code === 200) {
                Toast.show({
                    icon: 'success',
                    content: memberInfo?.admin === 1 ? '修改成功，已展示' : '修改成功，待审核'
                });
                closeEditPopup();
                getAlbumList();
                getPendingAuditCount();
            } else {
                Toast.show({ icon: 'fail', content: response.data?.msg || '修改失败' });
            }
        }).catch(() => {
            Toast.show({ icon: 'fail', content: '修改失败' });
        }).finally(() => {
            setEditSubmitting(false);
        });
    };

    return (
        <div className="album-page">
            <NavBar className="album-navbar" onBack={() => navigate(-1)}>
                球队相册
            </NavBar>

            <PullToRefresh onRefresh={async () => { getGameOptions(); getMemberOptions(); getAlbumList(); getPendingAuditCount(); }}>
                <div className="album-hero">
                    <div>
                        <div className="album-hero-kicker">PHOTO & VIDEO</div>
                        <div className="album-hero-title">记录每一场球</div>
                        <div className="album-hero-desc">按比赛场次归档，也可以用图片/视频标签快速搜索。</div>
                    </div>
                    <div className="album-hero-icon">📷</div>
                </div>

                <div className="album-filter-panel">
                    {memberInfo?.admin === 1 && pendingAuditCount > 0 && (
                        <button
                            type="button"
                            className="album-audit-alert"
                            onClick={() => {
                                setRecycleMode(false);
                                setStatusFilter('1');
                            }}
                        >
                            <span className="album-audit-alert-icon">!</span>
                            <span>
                                <strong>{pendingAuditCount} 条影像待审核</strong>
                                <em>点击切换到待审核列表</em>
                            </span>
                        </button>
                    )}
                    <SearchBar
                        className="album-search"
                        placeholder="输入标签搜索，例如：合影、进球"
                        value={tagName}
                        onChange={setTagName}
                    />
                    <button className="album-filter-game-field" type="button" onClick={openGamePicker}>
                        <span>{selectedGameLabel}</span>
                        <i>选择场次</i>
                    </button>
                    <button className="album-filter-game-field album-filter-member-field" type="button" onClick={openMemberPicker}>
                        <span>{selectedMemberLabel}</span>
                        <i>筛选队员</i>
                    </button>
                    <div className="album-filter-group">
                        <div className="album-filter-group-title">影像类型</div>
                        <Selector
                            className="album-media-selector album-media-selector--type"
                            options={MEDIA_TYPE_OPTIONS}
                            value={[selectedMediaType]}
                            onChange={(arr) => setSelectedMediaType(arr[0] || 'all')}
                            showCheckMark={false}
                        />
                    </div>
                    <div className="album-filter-group">
                        <div className="album-filter-group-title">审核状态</div>
                        <Selector
                            className="album-media-selector album-media-selector--status"
                            options={statusOptions}
                            value={[statusFilter]}
                            onChange={(arr) => {
                                setRecycleMode(false);
                                setStatusFilter(arr[0] || '0');
                            }}
                            showCheckMark={false}
                        />
                    </div>
                </div>

                <div className="album-section-head">
                    <div>
                        <div className="album-section-title">{recycleMode ? '回收站' : '影像列表'}</div>
                        <div className="album-section-count">
                            {loading ? '加载中...' : recycleMode ? `已隐藏 ${albumList.length} 条，可点击详情恢复` : `共 ${albumList.length} 条记录`}
                        </div>
                    </div>
                    <div className="album-section-actions">
                        {memberInfo?.admin === 1 && (
                            <Button
                                size="small"
                                className={`album-recycle-btn ${recycleMode ? 'active' : ''}`}
                                onClick={recycleMode ? closeRecycleBin : openRecycleBin}
                            >
                                {recycleMode ? '返回相册' : '回收站'}
                            </Button>
                        )}
                        {!isGuest && (
                            <Button
                                size="small"
                                className={`album-mine-filter-btn ${memberQuickFilter === 'mine' ? 'active' : ''}`}
                                onClick={toggleMineFilter}
                            >
                                我的
                            </Button>
                        )}
                        {!isGuest && (
                            <Button size="small" color="primary" onClick={() => navigate('/albumUpload')}>
                                上传影像
                            </Button>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className="album-loading-panel">
                        <SpinLoading color="primary" />
                        <span>正在加载影像...</span>
                    </div>
                ) : albumList.length > 0 ? (
                    <div className="album-media-grid">
                        {albumList.map(item => {
                            const mediaType = getMediaType(item);
                            const mediaUrl = getMediaUrl(item);
                            const tags = getMediaTagNames(item);
                            const bindGameName = getBindGameName(item);
                            const bindMemberNames = getBindMemberNames(item);
                            return (
                                <div key={item.id} className="album-media-card" onClick={() => openPreview(item)}>
                                    <div className="album-thumb-wrap">
                                        <Image src={buildThumbUrl(item)} fit="cover" className="album-thumb" />
                                        <div className={`album-type-badge album-type-badge--${mediaType === 2 ? 'video' : 'image'}`}>
                                            {mediaType === 2 ? '视频' : '图片'}
                                        </div>
                                        {mediaType === 2 && (
                                            <div className="album-play-mask">
                                                <img src={Icon.VIDEO_PALY} alt="播放" />
                                            </div>
                                        )}
                                        <div className="album-view-badge">
                                            <span>👁</span>
                                            <strong>{getViewCount(item)}</strong>
                                        </div>
                                    </div>
                                    <div className="album-card-body">
                                        <div className={`album-card-status-chip album-card-status-chip--${Number(item.status)}`}>
                                            {getStatusText(item)}
                                        </div>
                                        <div className="album-card-title">{getDisplayTitle(item)}</div>
                                        <div className="album-card-info-stack">
                                            {bindGameName && <div className="album-card-info-line"><span>比赛</span><strong>{bindGameName}</strong></div>}
                                            {bindMemberNames && <div className="album-card-info-line"><span>队员</span><strong>{bindMemberNames}</strong></div>}
                                            <div className="album-card-info-line"><span>标签</span><strong>{tags.length ? tags.join('、') : '未标记'}</strong></div>
                                        </div>
                                        {Number(item.status) === 2 && getAuditRemark(item) && (
                                            <div className="album-reject-reason album-reject-reason--card">
                                                <span>原因</span>
                                                <strong>{getAuditRemark(item)}</strong>
                                            </div>
                                        )}
                                        <div className="album-card-footer">
                                            <div className="album-card-meta">
                                                <span>👍 {getLikeCount(item)}</span>
                                                <span>👎 {getDislikeCount(item)}</span>
                                                <span>💬 {getCommentCount(item)}</span>
                                            </div>
                                        </div>
                                        {recycleMode && memberInfo?.admin === 1 && Number(item.status) === 3 && (
                                            <button
                                                type="button"
                                                className="album-card-restore-btn"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    auditMedia(item, 0);
                                                }}
                                            >
                                                恢复展示
                                            </button>
                                        )}
                                    </div>
                                    {!mediaUrl && <div className="album-card-error">资源地址缺失</div>}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <Empty description="没有找到对应影像" imageStyle={{ width: 90 }} />
                )}
            </PullToRefresh>

            <Popup
                visible={!!selectedItem}
                onMaskClick={() => setSelectedItem(null)}
                bodyStyle={{
                    borderTopLeftRadius: 18,
                    borderTopRightRadius: 18,
                    maxHeight: '88vh',
                    overflowY: 'auto',
                    background: '#FFFFFF'
                }}
            >
                {selectedItem && (
                    <div className="album-detail-popup">
                        <div className="album-preview-title album-preview-title--top">{getDisplayTitle(selectedItem)}</div>
                        <div className="album-preview-subtitle album-preview-subtitle--top">{getBindGameName(selectedItem) || '未绑定比赛'}</div>
                        {getMediaType(selectedItem) === 2 ? (
                            <video className="album-preview-video" src={getMediaUrl(selectedItem)} controls poster={buildThumbUrl(selectedItem)} />
                        ) : (
                            <img className="album-preview-image" src={getMediaUrl(selectedItem)} alt={getDisplayTitle(selectedItem)} />
                        )}
                        <div className="album-preview-info-panel">
                            <div className="album-preview-info-row">
                                <span>比赛</span>
                                <strong>{getBindGameName(selectedItem) || '未绑定比赛'}</strong>
                            </div>
                            {getBindMemberNames(selectedItem) && (
                                <div className="album-preview-info-row">
                                    <span>关联队员</span>
                                    <strong>{getBindMemberNames(selectedItem)}</strong>
                                </div>
                            )}
                            {getMediaTagNames(selectedItem).length > 0 && (
                                <div className="album-preview-info-row">
                                    <span>标签</span>
                                    <strong>{getMediaTagNames(selectedItem).join('、')}</strong>
                                </div>
                            )}
                            <div className="album-preview-info-row">
                                <span>上传人</span>
                                <strong>{getUploadMemberName(selectedItem)}</strong>
                            </div>
                            <div className="album-preview-info-row">
                                <span>状态</span>
                                <strong>{getStatusText(selectedItem)}</strong>
                            </div>
                            {Number(selectedItem.status) === 2 && getAuditRemark(selectedItem) && (
                                <div className="album-preview-info-row album-preview-info-row--reject">
                                    <span>不通过原因</span>
                                    <strong>{getAuditRemark(selectedItem)}</strong>
                                </div>
                            )}
                        </div>
                        {canEditMedia(selectedItem) && (
                            <div className="album-edit-entry">
                                {/* 媒体详情中的编辑入口，做成独立操作块便于管理员快速识别 */}
                                <button type="button" className="album-edit-action-btn" onClick={() => openEditMedia(selectedItem)}>
                                    <span className="album-edit-action-icon">✎</span>
                                    <span className="album-edit-action-text">
                                        <strong>修改信息</strong>
                                        <em>{memberInfo?.admin === 1 ? '保存后直接展示' : '保存后重新审核'}</em>
                                    </span>
                                </button>
                            </div>
                        )}
                        {isReadonlySubmission(selectedItem) ? (
                            <div className="album-readonly-tip">这是你的提交记录，可以修改信息后重新提交审核。</div>
                        ) : (
                            !isGuest && (
                                <div className="album-detail-actions">
                                    <Button
                                        size="small"
                                        className={Number(selectedItem.currentReactionType) === 1 ? 'album-action-active' : ''}
                                        onClick={() => handleReaction(selectedItem, 1)}
                                    >
                                        👍 喜欢 {getLikeCount(selectedItem)}
                                    </Button>
                                    <Button
                                        size="small"
                                        className={Number(selectedItem.currentReactionType) === 2 ? 'album-action-active dislike' : ''}
                                        onClick={() => handleReaction(selectedItem, 2)}
                                    >
                                        👎 不喜欢 {getDislikeCount(selectedItem)}
                                    </Button>
                                </div>
                            )
                        )}
                        {!isGuest && memberInfo?.admin === 1 && (
                            <div className="album-admin-actions">
                                {Number(selectedItem.status) === 1 && <Button size="small" color="success" onClick={() => auditMedia(selectedItem, 0)}>审核通过</Button>}
                                {Number(selectedItem.status) === 1 && <Button size="small" color="warning" onClick={() => auditMedia(selectedItem, 2)}>不通过</Button>}
                                {[2, 3].includes(Number(selectedItem.status)) && <Button size="small" color="primary" onClick={() => auditMedia(selectedItem, 0)}>重新发布</Button>}
                                {Number(selectedItem.status) !== 3 && <Button size="small" color="danger" onClick={() => auditMedia(selectedItem, 3)}>删除</Button>}
                            </div>
                        )}
                        <div className="album-comment-title">评论</div>
                        <div className="album-comment-list">
                            {comments.length > 0 ? comments.map(comment => (
                                <div key={comment.id} className="album-comment-item">
                                    <div className="album-comment-user">{memberNameMap[comment.memberId]  || comment.member_name || '队友'}</div>
                                    <div className="album-comment-content">{comment.content}</div>
                                </div>
                            )) : <div className="album-comment-empty">暂无评论</div>}
                        </div>
                        {!isGuest && !isReadonlySubmission(selectedItem) && (
                            <div className="album-comment-input">
                                <Input placeholder="写一句评论" value={commentText} onChange={setCommentText} />
                                <Button size="small" color="primary" onClick={submitComment}>发送</Button>
                            </div>
                        )}
                    </div>
                )}
            </Popup>

            <Popup
                visible={editVisible}
                onMaskClick={closeEditPopup}
                bodyStyle={{
                    borderTopLeftRadius: 20,
                    borderTopRightRadius: 20,
                    maxHeight: '88vh',
                    overflowY: 'auto',
                    background: '#FFFFFF'
                }}
            >
                <div className="album-edit-popup">
                    <div className="album-picker-head album-edit-head">
                        <button type="button" onClick={closeEditPopup}>取消</button>
                        <strong>修改影像信息</strong>
                        <button type="button" onClick={submitEditMedia}>{editSubmitting ? '保存中' : '保存'}</button>
                    </div>
                    <div className="album-upload-form album-edit-form">
                        <div className="album-form-section-title">绑定信息</div>
                        <div className="album-field-label">比赛场次</div>
                        <button className="album-picker-field" type="button" onClick={() => openEditPicker('game')}>
                            <span>{getOptionLabel(editGameOptions, editForm.gameId) || '不绑定比赛'}</span>
                            <i>选择</i>
                        </button>

                        <div className="album-field-label">比赛事件</div>
                        <button className="album-picker-field" type="button" onClick={() => openEditPicker('event')}>
                            <span>{getOptionLabel(editEventOptions, editForm.eventId) || '不绑定事件'}</span>
                            <i>选择</i>
                        </button>

                        <div className="album-field-label">关联队员</div>
                        <button className="album-picker-field" type="button" onClick={() => openEditPicker('member')}>
                            <span>{getMultiOptionLabel(editMemberOptions, editForm.memberId) || '不绑定队员'}</span>
                            <i>选择</i>
                        </button>

                        <div className="album-field-label">展示名称</div>
                        <Input
                            className="album-form-input"
                            placeholder="图片/视频名称"
                            value={editForm.remark}
                            onChange={(value) => setEditForm(prev => ({ ...prev, remark: value }))}
                        />

                        <div className="album-field-label">选择标签</div>
                        <div className="album-tag-picker">
                            {/* 标签直接展示，管理员可在末尾新增或删除标签 */}
                            {tagOptions.length > 0 || memberInfo?.admin === 1 ? (
                                <div className="album-tag-picker-options">
                                    {tagOptions.map(tag => (
                                        <span key={tag.id} className={`album-upload-tag-shell ${(editForm.tagIds || []).includes(tag.id) ? 'active' : ''}`}>
                                            <button
                                                type="button"
                                                className="album-upload-tag-option"
                                                onClick={() => toggleEditTag(tag.id)}
                                            >
                                                {tag.tagName}
                                            </button>
                                            {memberInfo?.admin === 1 && (
                                                <button type="button" className="album-upload-tag-delete" onClick={() => deleteEditTag(tag.id)}>
                                                    ×
                                                </button>
                                            )}
                                        </span>
                                    ))}
                                    {memberInfo?.admin === 1 && (
                                        <button type="button" className="album-tag-add-chip" onClick={addEditTag}>
                                            + 增加标签
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="album-tag-picker-empty">暂无可选标签</div>
                            )}
                        </div>

                        <div className="album-edit-status-tip">
                            {memberInfo?.admin === 1 ? '保存后状态会自动改为已展示。' : '保存后状态会自动改为待审核，管理员审核后展示。'}
                        </div>
                        <Button block color="primary" loading={editSubmitting} className="album-submit-btn" onClick={submitEditMedia}>
                            保存修改
                        </Button>
                    </div>
                </div>
            </Popup>

            <Popup
                visible={!!currentEditPicker}
                onMaskClick={closeEditPicker}
                bodyStyle={{ borderTopLeftRadius: 20, borderTopRightRadius: 20 }}
            >
                {currentEditPicker && (
                    <div className="album-picker-popup">
                        <div className="album-picker-head">
                            <button type="button" onClick={closeEditPicker}>取消</button>
                            <strong>{currentEditPicker.title}</strong>
                            <button type="button" onClick={confirmEditPicker}>确认</button>
                        </div>
                        <SearchBar
                            className="album-picker-search"
                            placeholder={currentEditPicker.placeholder}
                            value={editPickerSearch}
                            onChange={setEditPickerSearch}
                        />
                        <div className={`album-picker-list ${editPickerType === 'member' ? 'album-member-picker-grid' : ''}`}>
                            {filteredEditPickerOptions.length > 0 ? filteredEditPickerOptions.map(item => (
                                <button
                                    key={item.value}
                                    type="button"
                                    className={`album-picker-option ${editPickerType === 'event' ? 'album-picker-option--event' : ''} ${isEditOptionSelected(item.value) ? 'active' : ''}`}
                                    onClick={() => editPickerType === 'member' ? toggleEditMemberDraft(item.value) : setEditPickerDraftValue(item.value)}
                                >
                                    {renderEditPickerOptionContent(item)}
                                    <i>{isEditOptionSelected(item.value) ? '已选' : ''}</i>
                                </button>
                            )) : (
                                <Empty description="没有匹配的数据" imageStyle={{ width: 80 }} />
                            )}
                        </div>
                    </div>
                )}
            </Popup>

            <Popup
                visible={gamePickerVisible}
                onMaskClick={() => setGamePickerVisible(false)}
                bodyStyle={{ borderTopLeftRadius: 18, borderTopRightRadius: 18, overflow: 'hidden' }}
            >
                <div className="album-picker-popup album-game-picker-popup">
                    <div className="album-picker-head">
                        <button type="button" onClick={() => setGamePickerVisible(false)}>取消</button>
                        <strong>选择比赛场次</strong>
                        <button type="button" onClick={confirmGamePicker}>确认</button>
                    </div>
                    <SearchBar
                        className="album-picker-search"
                        placeholder="搜索比赛名称或时间"
                        value={gamePickerKeyword}
                        onChange={setGamePickerKeyword}
                    />
                    <div className="album-picker-list">
                        {filteredGameOptions.length > 0 ? filteredGameOptions.map(item => (
                            <button
                                key={item.value}
                                type="button"
                                className={`album-picker-option album-game-picker-option ${gamePickerDraft === item.value ? 'active' : ''}`}
                                onClick={() => setGamePickerDraft(item.value)}
                            >
                                <span>{item.label}</span>
                                {gamePickerDraft === item.value && <i>已选</i>}
                            </button>
                        )) : (
                            <Empty description="没有找到比赛" imageStyle={{ width: 72 }} />
                        )}
                    </div>
                </div>
            </Popup>

            <Popup
                visible={memberPickerVisible}
                onMaskClick={() => setMemberPickerVisible(false)}
                bodyStyle={{ borderTopLeftRadius: 18, borderTopRightRadius: 18, overflow: 'hidden' }}
            >
                <div className="album-picker-popup album-member-picker-popup">
                    <div className="album-picker-head">
                        <button type="button" onClick={() => setMemberPickerVisible(false)}>取消</button>
                        <strong>筛选关联队员</strong>
                        <button type="button" onClick={confirmMemberPicker}>确认</button>
                    </div>
                    <SearchBar
                        className="album-picker-search"
                        placeholder="搜索队员姓名"
                        value={memberPickerKeyword}
                        onChange={setMemberPickerKeyword}
                    />
                    <div className="album-picker-list album-member-picker-grid">
                        {filteredMemberOptions.length > 0 ? filteredMemberOptions.map(item => (
                            <button
                                key={item.value}
                                type="button"
                                className={`album-picker-option album-member-picker-option ${memberPickerDraft === item.value ? 'active' : ''}`}
                                onClick={() => setMemberPickerDraft(item.value)}
                            >
                                {renderMemberOptionContent(item)}
                                {memberPickerDraft === item.value && <i>已选</i>}
                            </button>
                        )) : (
                            <Empty description="没有找到队员" imageStyle={{ width: 72 }} />
                        )}
                    </div>
                </div>
            </Popup>
        </div>
    );
}
